/**
 * Repository - LocalStorageTransactionRepository
 *
 * Implémentation du repository de transactions utilisant localStorage
 *
 * IMPORTANT: Append-only - pas de update ni delete
 */

import { ITransactionRepository } from '../../application/ports/output/ITransactionRepository';
import { Transaction } from '../../domain/entities/Transaction';
import { AccountNumber } from '../../domain/value-objects/AccountNumber';
import { TransactionId } from '../../domain/value-objects/TransactionId';

const STORAGE_KEY = 'biotransac_transactions';

export class LocalStorageTransactionRepository implements ITransactionRepository {
  /**
   * Sauvegarde une transaction
   */
  async save(transaction: Transaction): Promise<void> {
    const transactions = await this.loadAll();
    transactions.push(transaction);
    this.persist(transactions);
  }

  /**
   * Récupère une transaction par son ID
   */
  async findById(id: TransactionId): Promise<Transaction | null> {
    const transactions = await this.loadAll();
    return transactions.find(t => t.id.equals(id)) || null;
  }

  /**
   * Récupère toutes les transactions d'un compte
   */
  async findByAccount(accountNumber: AccountNumber): Promise<Transaction[]> {
    const transactions = await this.loadAll();
    return transactions.filter(t => t.accountNumber.equals(accountNumber));
  }

  /**
   * Récupère toutes les transactions
   */
  async findAll(): Promise<Transaction[]> {
    return this.loadAll();
  }

  /**
   * Récupère les transactions dans une plage de dates
   */
  async findByDateRange(startDate: Date, endDate: Date): Promise<Transaction[]> {
    const transactions = await this.loadAll();
    return transactions.filter(t => {
      const date = t.createdAt;
      return date >= startDate && date <= endDate;
    });
  }

  /**
   * Compte le nombre total de transactions
   */
  async count(): Promise<number> {
    const transactions = await this.loadAll();
    return transactions.length;
  }

  /**
   * Compte les transactions d'un compte
   */
  async countByAccount(accountNumber: AccountNumber): Promise<number> {
    const transactions = await this.findByAccount(accountNumber);
    return transactions.length;
  }

  /**
   * Charge toutes les transactions depuis localStorage
   */
  private loadAll(): Transaction[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      return parsed.map((json: any) => Transaction.fromJSON(json));
    } catch (error) {
      console.error('Erreur lors du chargement des transactions:', error);
      return [];
    }
  }

  /**
   * Persiste les transactions dans localStorage
   */
  private persist(transactions: Transaction[]): void {
    try {
      const serialized = transactions.map(t => t.toJSON());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des transactions:', error);
      throw new Error('Échec de la persistence des transactions');
    }
  }

  /**
   * Supprime toutes les transactions (pour tests uniquement)
   */
  async clear(): Promise<void> {
    localStorage.removeItem(STORAGE_KEY);
  }
}
